// @ts-nocheck

import useGraphData from '@/hooks/useGraphData';
import useTradingAccount from '@/hooks/useTradingAccount';
import { PortfolioPeriodType } from '@/lib/TradingAccount/types';
import { ParsedPortfolioHistory, PortfolioPeriod } from '@/pages/wallet/PortfolioGraph/utils';
import clsx from 'clsx';
import _ from 'lodash';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Line, LineChart, ResponsiveContainer, Tooltip, YAxis } from 'recharts';
import { CategoricalChartFunc } from 'recharts/types/chart/generateCategoricalChart';
import Loader from '../../components/Loader';
import Analytics from '../../system/Analytics';
import { IndividualAssetEvents } from '../../system/Analytics/events/IndividualAssetEvents';
import { PortfolioEvents } from '../../system/Analytics/events/PortfolioEvents';
import MathUtils from '../../utils/MathUtils';
import StringUtils from '../../utils/StringUtils';
import StyleUtils from '../../utils/StyleUtils';
import PeriodTabs from './PeriodTabs';
import { GraphDataResponse, Period, PeriodType, Point } from './types';
import { formatCursorTimestamp, getGraphWidth } from './utils';
import MarketService from '@/lib/Market';
import moment from "moment";
import Text from '@/components/Text';

interface IGraphProps {
  symbol?: string;
  periods: (Period | PortfolioPeriod)[];
  totalWidth: number;
  totalHeight: number;
  fetchData: (activePeriodType: PeriodType | PortfolioPeriodType) => Promise<GraphDataResponse>;
  setLatestPrice?: (latestPrice: number) => void;
  emptyState?: ReactNode | string;
  isPortfolioGraph?: boolean;
  queryKeyPrefix?: string;
  chartColor?: Record<string, string>
}

export default function Graph({
  symbol,
  periods,
  totalWidth,
  totalHeight,
  fetchData,
  setLatestPrice,
  emptyState,
  isPortfolioGraph = false,
  queryKeyPrefix = 'graphData',
  chartColor = {
    lineStroke: '#FF4F83'
  }
}: IGraphProps) {
  const [activePoint, setActivePoint] = useState<Point>();
  const [graphDataError, setGraphDataError] = useState<string>();
  const [width, setWidth] = useState(0);
  const router = useRouter();
  const [activePeriod, setActivePeriod] = useState<Period | PortfolioPeriod>();
  const {
    data: graphData,
    isLoading,
    isFetching,
    status,
    isError,
  } = useGraphData(queryKeyPrefix, activePeriod?.value, fetchData, symbol);
  const { data: tradingAccount } = useTradingAccount(isPortfolioGraph);
  const lastPoint = graphData?.points?.length && graphData?.points[graphData?.points.length - 1].y;
  const currentEquity = useMemo(() => {
    let equity = (tradingAccount?.equity || tradingAccount?.last_equity || 0);
    if (equity < 0) {
      equity = 0;
    }
    return equity || lastPoint;
  }, [lastPoint, tradingAccount?.equity, tradingAccount?.last_equity]);
 
  const showPreviousClosePriceLine = !isPortfolioGraph && activePeriod?.value === PeriodType._1D;

  const calculateGraphWidth = useCallback(async () => {
    const width = await getGraphWidth(activePeriod, totalWidth, isPortfolioGraph);
    setWidth(width);
  }, [activePeriod, isPortfolioGraph, totalWidth]);

  const getFormattedChange = () => {
    const lastMarketDate = MarketService.getLastMarketDate();
    const isToday = moment(lastMarketDate).isSame(moment(), 'day');
    const numDaysAgo = moment().diff(lastMarketDate, 'days');

    const labelText = activePeriod?.value === PeriodType._1D ? (
      isToday ?
      'Today' :
      numDaysAgo > 1 ?
      `Last ${lastMarketDate.format('dddd')}` :
      'Yesterday'
    ) : activePeriod?.displayText;

    // Reference value is the starting point for which to calculat the percent change
    let referenceVal = 0;
    referenceVal = showPreviousClosePriceLine ? graphData?.prevClosePrice : graphData?.points?.length && graphData?.points[0].y;

    if (isPortfolioGraph && activePeriod?.value === PortfolioPeriodType._1D) {
      const activeProfitLoss = (graphData as ParsedPortfolioHistory)?.return?.dollar || (graphData?.points?.length && graphData?.points[0].y);
      referenceVal = currentEquity - activeProfitLoss; 
    }

    const activeY = activePoint?.y as number;
    const dollarChange = activeY - referenceVal;
    const percentChange = activeY > 0 ? MathUtils.percentChange(referenceVal, activeY) : 0;

    return (
        <div
          className={clsx(
            'flex items-center space-x-2',
            StyleUtils.getChangeStyle(percentChange)
          )}
        >
          {StyleUtils.getChangePrefix(percentChange as number)}
          <Text>
            {' '}
            {StringUtils.USD(dollarChange)} (
            {StringUtils.signNumber(MathUtils.round(percentChange, 0.01) / 100, 'percent')})
              
          </Text>
          <Text className='text-secondary ml-2'>{labelText}</Text>
        </div>
    );
  };

  useEffect(() => {
    calculateGraphWidth();
  }, [symbol, activePeriod]);

  useEffect(() => {
    const period = periods.find((p) => p.displayVal === router.query?.period) || periods[0];
    setActivePeriod(period);
  }, [router.isReady]);

  useEffect(() => {
    if (!isFetching && !isLoading) {
      if (graphData?.points?.length > 0) {
        setActivePoint(graphData?.points[graphData?.points.length - 1]);
        if (setLatestPrice) {
          setLatestPrice(graphData?.points[graphData?.points.length - 1].y);
        }
      } else {
        setGraphDataError('No data found. Try again later.');
      }
    }
  }, [symbol, isLoading, isFetching]);

  const onMouseMove: CategoricalChartFunc = ({
    isTooltipActive,
    activeTooltipIndex,
    chartX,
    chartY,
    activePayload,
    ...rest
  }) => {
    if (
      isTooltipActive &&
      activeTooltipIndex !== undefined &&
      !_.isEqual(activePayload?.[0].payload, activePoint)
    ) {
      // When active tool tip index changes, set new active point
      setActivePoint(activePayload?.[0].payload);
    }
  };

  const onMouseLeave: CategoricalChartFunc = (props) => {
    // When the user takes mouse off graph, reset the active text to the most recent point
    setActivePoint(graphData?.points[graphData?.points?.length - 1]);
  };

  const CustomizedDot = (props) => {
    const { cx, cy, stroke, payload, value } = props;

    if (
      activePeriod?.value === PeriodType._1D &&
      width < totalWidth &&
      payload.x === graphData?.points[graphData?.points?.length - 1].x
    ) {
      return <circle cx={cx - 2.5} cy={cy - 2.5} fill={chartColor.lineStroke} r="5" />;
    }

    return null;
  };

  const CustomTooltip = ({ active, payload, label, ...rest }) => {
    if (active && payload && payload.length) {
      return (
        <div tabIndex={-1} className="focus:none">
          <p className="text-md">{formatCursorTimestamp(activePeriod, payload[0].payload?.x)}</p>
        </div>
      );
    }

    return null;
  };

  const onChangePeriod = (period: Period) => {
    Analytics.track(
      symbol ? IndividualAssetEvents.SWITCH_GRAPH_PERIOD : PortfolioEvents.SWITCH_GRAPH_PERIOD,
      period
    );
    router.query.period = period.displayVal;
    router.push(router);
    setActivePeriod(period);
  };
  let prevLineValue=0;
  if (!isLoading && !graphDataError && !isError && graphData?.points?.length > 0) {
    const minVal = Math.min(...graphData?.points?.map((p) => p.y));
    const maxVal = Math.max(...graphData?.points?.map((p) => p.y));
    const temp = Math.round(((graphData?.prevClosePrice-minVal)/(maxVal-minVal))*totalHeight);
    if (temp>totalHeight) {
      prevLineValue=totalHeight;
    } else if (temp<0) {
      prevLineValue=0;
    } else {
      prevLineValue=temp;
    }
  }
  return (
    <div className='@container/graph'>
      <h1 className="text-title-1 @lg/graph:text-huge-2">{StringUtils.USD(activePoint?.y as number)}</h1>
      <div className="flex justify-between pr-2 [&_span]:text-sm @lg/graph:[&_span]:text-base">
        {getFormattedChange()}
        <div className="hidden @lg/graph:block">
          <PeriodTabs periods={periods} activePeriod={activePeriod} onChange={onChangePeriod} />
        </div>
      </div>
      <div className="h-[230px] w-full mt-12 border-b border-l border-primary">
        {!isLoading && !graphDataError && !isError && graphData?.points?.length > 0 ? (
          <div> 
            <ResponsiveContainer width={`${(width / totalWidth) * 100}%`} height={totalHeight}>
            <LineChart
              width="100%"
              height={totalHeight}
              data={graphData?.points}
              margin={{
                top: 10,
                right: 32,
                bottom: 10,
                left: -58,
              }}
              onMouseMove={onMouseMove}
              onMouseLeave={onMouseLeave}
            >
              <Tooltip
                isAnimationActive={false}
                position={{ y: -20 }}
                content={<CustomTooltip />}
                offset={activePeriod.tooltipOffset}
              />
              <YAxis
                domain={[
                  Math.min(...graphData?.points?.map((p) => p.y)),
                  Math.max(...graphData?.points?.map((p) => p.y)),
                ]}
                display="none"
              />
              <Line
                type="monotone"
                dataKey="y"
                dot={<CustomizedDot />}
                strokeWidth={3}
                stroke={chartColor.lineStroke}
                activeDot={{ stroke: 'white', strokeWidth: 2.75, r: 8, strokeDasharray: '' }}
                tooltipType="none"
              />
            </LineChart>
          </ResponsiveContainer>
          {showPreviousClosePriceLine && (
            <div
              style={{ transform: `translateY(${-1*prevLineValue}px)`, width: '100%', borderTop: '1px dashed gray'}}
            />)}
          </div>
        ) : (
          <div className="flex justify-center items-center h-full">
            {graphDataError ? emptyState || graphDataError : <Loader className="mx-auto" />}
          </div>
        )}
      </div>
      <div className="@lg/graph:hidden mt-4 [&_button]:text-xs [&_button]:h-8 [&_button]:px-3 @md/graph:[&_button]:text-sm">
        <PeriodTabs periods={periods} activePeriod={activePeriod} onChange={onChangePeriod} />
      </div>
    </div>
  );
}
